import { FilterOperator } from '@app/utils/filter-operator';
import { ChatDatasetArtifact, ChatMapArtifact } from '../models/orchestrator-chat.models';

const METRIC_PRIORITY = [
  'total_area_ha',
  'recognized_area_ha',
  'declared_area_ha',
  'yield_forecast_t_ha',
  'yield_t_ha',
  'area_ha',
  'loss_area_ha',
  'loss_t',
  'anomaly_count',
  'open_anomaly_count',
  'risk_index'
];

const METRIC_LABELS: Record<string, string> = {
  total_area_ha: 'Площадь, га',
  recognized_area_ha: 'Распознанная площадь, га',
  declared_area_ha: 'Заявленная площадь, га',
  yield_forecast_t_ha: 'Урожайность, т/га',
  yield_t_ha: 'Урожайность, т/га',
  area_ha: 'Площадь, га',
  loss_area_ha: 'Площадь потерь, га',
  loss_t: 'Потери, т',
  anomaly_count: 'Аномалии',
  open_anomaly_count: 'Открытые аномалии',
  risk_index: 'Индекс риска',
  density_ha_per_km2: 'Плотность, га/км²'
};

/** Synthetic metric: area column divided by the district polygon area. */
export const DENSITY_METRIC = 'density_ha_per_km2';

/** Same swatches as the GIS plan and fact season layers. */
export const PLAN_SEASON_COLOR = '#15803d';
export const FACT_SEASON_COLOR = '#4287f5';

const LEGEND_STOP_COUNT = 5;
const EARTH_RADIUS_KM = 6371.0088;

const ID_COLUMNS = new Set([
  'region_id',
  'field_id',
  'geo_id',
  'crop_id',
  'season_id',
  'id',
  'geom',
  'centroid_lat',
  'centroid_lon'
]);

/** Point used when a row has a centroid but no contour. */
export interface AnalyticsMapPoint {
  id: string;
  name: string;
  lat: number;
  lng: number;
  fillColor: string;
  popup: string;
  tooltip: string;
}

const WORLD_HALF = 20037508.342789244;
const WEATHER_LAYER_NAME = /\b(?:[A-Za-z0-9_]+:)?((?:Era|Ibm|Ugm)Weather[A-Za-z]+\d{4,8})\b/g;
const INDEX_LAYER_NAME = /^(GNDVI|MNDWI|NDMI|NDVI|NDWI|ORVI|OSAVI|ARVI|EVI)\d{8}$/i;

export interface LeafletGeometry {
  coordinates: [number, number][];
  wktCoordinates: [number, number][][][];
}

/** `area` is a field or district coloured by the metric; `anomaly` is an anomaly polygon coloured by its type. */
export type AnalyticsPolygonKind = 'area' | 'anomaly';

export interface AnalyticsMapPolygon {
  id: string;
  name: string;
  kind: AnalyticsPolygonKind;
  coordinates: [number, number][];
  wktCoordinates: [number, number][][][];
  fillColor: string;
  strokeColor: string;
  popup: string;
  tooltip: string;
  /** District id when the polygon is a region the user can open. */
  regionId: string | null;
}

/** One entry of the anomaly legend: type label and the colour its polygons use. */
export interface AnalyticsAnomalyLegendItem {
  type: string;
  color: string;
}

export const INDEX_TYPE_OPTIONS = ['Ndvi', 'Evi', 'Gndvi', 'Ndwi', 'Ndmi', 'Osavi', 'Arvi', 'Mndwi', 'Orvi'];

const ANOMALY_PALETTE = ['#d9534f', '#f0ad4e', '#8e44ad', '#2980b9', '#16a085', '#c0392b', '#7f8c8d'];
const ANOMALY_STROKE = '#8a1c1c';

export interface AnalyticsWmsLayer {
  id: string;
  wmsUrl: string;
  layerName: string;
  style: string | null;
  time: string | null;
  bbox: string | null;
}

export interface AnalyticsLegendStop {
  value: number;
  color: string;
}

export interface AnalyticsMapLegend {
  metric: string;
  label: string;
  hint: string;
  min: number;
  max: number;
  stops: AnalyticsLegendStop[];
}

/** One filter of the season bbox query. */
export interface SeasonLayerFilter {
  filterField: string;
  filterOperator: FilterOperator;
  filterValue: unknown;
}

export interface AnalyticsMapScene {
  polygons: AnalyticsMapPolygon[];
  points: AnalyticsMapPoint[];
  wms: AnalyticsWmsLayer[];
  legend: AnalyticsMapLegend | null;
  anomalyLegend: AnalyticsAnomalyLegendItem[];
  bounds: [number, number][];
  warnings: string[];
}

const WEATHER_TOOLS = new Set([
  'get_weather_metrics',
  'get_weather_aggregate',
  'get_weather_forecast',
  'get_weather_forecast_aggregate'
]);

const INDEX_TYPE_BY_WIRE: Record<string, string> = {
  ndvi: 'Ndvi',
  evi: 'Evi',
  gndvi: 'Gndvi',
  mndwi: 'Mndwi',
  ndwi: 'Ndwi',
  ndmi: 'Ndmi',
  orvi: 'Orvi',
  osavi: 'Osavi',
  arvi: 'Arvi'
};

const WEATHER_TOKEN_BY_COLUMN: Record<string, string> = {
  precipitation_mm: 'SumOfFallout',
  precipitation_mm_total: 'SumOfFallout',
  solar_radiation_mj: 'SumOfSolarRadiation',
  solar_radiation_mj_total: 'SumOfSolarRadiation',
  solar_radiation_mj_per_day: 'SumOfSolarRadiation',
  humidity_pct: 'Humidity',
  wind_speed_mean_ms: 'WindSpeed',
  tavg_c: 'TemperatureAverage',
  tmax_c: 'TemperatureAverage',
  tmin_c: 'TemperatureAverage',
  t2m_mean_c: 'TemperatureAverage',
  t2m_min_c: 'TemperatureAverage',
  t2m_max_c: 'TemperatureAverage'
};

export interface RasterPeriod {
  from: string;
  to: string;
}

export interface RasterRequest {
  kind: 'weather' | 'index';
  weatherToken: string | null;
  indexTypes: string[];
}

export function mapsHaveGeography(maps: ChatMapArtifact[] | null | undefined): boolean {
  return (maps ?? []).some((map) => mapHasDrawnGeography(map));
}

/** True when at least one table row carries a contour or a centroid the map can place. */
export function datasetHasGeography(dataset: ChatDatasetArtifact | null | undefined): boolean {
  return (dataset?.preview_rows ?? []).some((row) => geoJsonToLeaflet(row['geom']) !== null || readCentroid(row) !== null);
}

/** Lat/lng of a row centroid: find_fields returns centroid_lat / centroid_lon, other tools may use lat / lon. */
export function readCentroid(row: Record<string, unknown> | undefined): [number, number] | null {
  if (!row) {
    return null;
  }
  const lat = readNumber(row['centroid_lat']) ?? readNumber(row['lat']) ?? readNumber(row['latitude']);
  const lng = readNumber(row['centroid_lon']) ?? readNumber(row['centroid_lng']) ?? readNumber(row['lon']) ?? readNumber(row['lng']) ?? readNumber(row['longitude']);
  if (lat === null || lng === null || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
    return null;
  }
  return [lat, lng];
}

/**
 * Identity of what the map would draw. Two answers about different fields can have the same
 * number of rows and maps, so the key is built from row ids and geometry, not from counts.
 */
export function sceneKey(
  maps: ChatMapArtifact[] | null | undefined,
  dataset: ChatDatasetArtifact | null | undefined,
  metric: string | null | undefined
): string {
  const parts: string[] = [metric ?? '', dataset?.dataset_id ?? '', dataset?.tool ?? ''];
  for (const row of dataset?.preview_rows ?? []) {
    parts.push(rowIdentity(row));
  }
  for (const row of dataset?.map_rows ?? []) {
    parts.push(`m:${rowIdentity(row)}`);
  }
  (maps ?? []).forEach((map, index) => {
    parts.push(`${index}:${map.kind}:${map.tool}:${map.geo_id ?? ''}`);
    const items = map.kind === 'districts' ? map.districts : map.kind === 'features' ? map.features : null;
    for (const item of items ?? []) {
      parts.push(rowIdentity(item));
    }
    if (map.wms) {
      parts.push(`${text(map.wms['layer_name']) ?? ''}@${text(map.wms['time_dimension']) ?? ''}`);
    }
  });
  return parts.join('|');
}

function rowIdentity(row: Record<string, unknown>): string {
  const id = text(row['field_id']) ?? text(row['region_id']) ?? text(row['anomaly_id']) ?? text(row['id']) ?? '';
  const geom = row['geom'];
  const geomSize = geom === null || geom === undefined
    ? 0
    : typeof geom === 'string' ? geom.length : JSON.stringify(geom).length;
  const centroid = readCentroid(row);
  return `${id}#${geomSize}#${centroid ? centroid.join(',') : ''}`;
}

export function toIsoDay(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (iso) {
    return `${iso[1]}-${iso[2]}-${iso[3]}`;
  }
  const compact = /^(\d{4})(\d{2})(\d{2})$/.exec(value.trim());
  if (compact) {
    return `${compact[1]}-${compact[2]}-${compact[3]}`;
  }
  return null;
}

export function datasetPeriod(dataset: ChatDatasetArtifact | null | undefined): RasterPeriod | null {
  const period = dataset?.meta?.['period'];
  if (!period || typeof period !== 'object') {
    return null;
  }
  const from = toIsoDay((period as { from?: unknown }).from);
  const to = toIsoDay((period as { to?: unknown }).to);
  if (!from || !to) {
    return null;
  }
  return from <= to ? { from, to } : { from: to, to: from };
}

export function rowIsoDays(dataset: ChatDatasetArtifact | null | undefined): string[] {
  const days = new Set<string>();
  for (const row of dataset?.preview_rows ?? []) {
    const day = toIsoDay(row['date']) ?? toIsoDay(row['period_start']);
    if (day) {
      days.add(day);
    }
  }
  return [...days].sort();
}

export function weatherTokenFor(metric: string | null | undefined): string | null {
  if (!metric) {
    return null;
  }
  return WEATHER_TOKEN_BY_COLUMN[metric] ?? null;
}

export function rasterRequest(
  dataset: ChatDatasetArtifact | null | undefined,
  selectedMetric?: string | null
): RasterRequest | null {
  if (!dataset) {
    return null;
  }
  const keys = datasetFieldKeys(dataset);
  const indexTypes = indexTypesFrom(dataset);
  const indexTool = dataset.tool === 'get_vegetation_index' || keys.includes('index_type');
  if (indexTool && indexTypes.length > 0) {
    return { kind: 'index', weatherToken: null, indexTypes };
  }

  const weatherColumns = keys.filter((column) => weatherTokenFor(column));
  const weatherTool = WEATHER_TOOLS.has(dataset.tool);
  if (!weatherTool && weatherColumns.length === 0) {
    return null;
  }
  const selected = weatherTokenFor(selectedMetric);
  const token = selected ?? (weatherColumns.length > 0 ? weatherTokenFor(weatherColumns[0]) : null);
  if (!token) {
    return null;
  }
  return { kind: 'weather', weatherToken: token, indexTypes: [] };
}

export function datasetOffersRaster(dataset: ChatDatasetArtifact | null | undefined): boolean {
  return rasterRequest(dataset) !== null;
}

/**
 * True when the answer is about concrete fields: the index layer is then offered on its own,
 * even though the table has no index or weather column to infer it from.
 */
export function isFieldScope(
  maps: ChatMapArtifact[] | null | undefined,
  dataset: ChatDatasetArtifact | null | undefined
): boolean {
  if (dataset?.tool === 'find_fields' || dataset?.tool?.startsWith('get_field_') === true) {
    return true;
  }
  if ((dataset?.preview_rows ?? []).some((row) => text(row['field_id']) !== null)) {
    return true;
  }
  return (maps ?? []).some((map) =>
    map.kind === 'features' && (map.features ?? []).some((item) => text(item['field_id']) !== null)
  );
}

/** The most recent catalog days when the answer has no period of its own (a field, not a time series). */
export function recentCatalogDays(catalogDays: string[], limit = 40): string[] {
  const days = uniqueDays(catalogDays);
  return days.slice(Math.max(0, days.length - limit));
}

/** Years to scan in the tile catalog when the dataset carries no dates: this year and the one before. */
export function recentYears(today = new Date()): number[] {
  const year = today.getFullYear();
  return [year - 1, year];
}

/** One anomaly polygon, as opposed to a district row that merely counts anomalies. */
function isAnomalyRow(row: Record<string, unknown>): boolean {
  if (text(row['anomaly_id']) !== null) {
    return true;
  }
  return text(row['anomaly_type']) !== null && readNumber(row['anomaly_count']) === null && readNumber(row['open_anomaly_count']) === null;
}

function anomalyColor(type: string, palette: Map<string, string>): string {
  const known = palette.get(type);
  if (known) {
    return known;
  }
  const color = ANOMALY_PALETTE[palette.size % ANOMALY_PALETTE.length];
  palette.set(type, color);
  return color;
}

function anomalyPopup(row: Record<string, unknown>): string {
  const type = text(row['anomaly_type']) ?? 'Аномалия';
  const parts = [`<strong>${escapeHtml(type)}</strong>`];
  const detected = toIsoDay(row['detected_date']) ?? toIsoDay(row['original_date']);
  if (detected) {
    parts.push(`Обнаружена: ${formatIsoDay(detected)}`);
  }
  const end = toIsoDay(row['end_date']);
  parts.push(end ? `Закрыта: ${formatIsoDay(end)}` : 'Открыта');
  const area = readNumber(row['loss_area_ha']) ?? readNumber(row['area_ha']);
  if (area !== null) {
    parts.push(`Площадь: ${formatMetric(area)} га`);
  }
  return parts.join('<br>');
}

/**
 * Days the catalog can draw. A stored period keeps only days inside it.
 * Without a period, a day must also appear on a table row. No overlap means no tiles.
 */
export function catalogDaysInWindow(
  catalogDays: string[],
  period: RasterPeriod | null,
  rowDays: string[]
): string[] {
  const catalog = uniqueDays(catalogDays);
  if (period) {
    return catalog.filter((day) => day >= period.from && day <= period.to);
  }
  const rows = new Set(uniqueDays(rowDays));
  return catalog.filter((day) => rows.has(day));
}

/** Latest day in the window: the most recent pass or weather day is what the user usually wants first. */
export function defaultCatalogDay(days: string[]): string | null {
  return days.length > 0 ? days[days.length - 1] : null;
}

export function yearsCovering(period: RasterPeriod | null, rowDays: string[]): number[] {
  if (period) {
    const start = Number(period.from.slice(0, 4));
    const end = Number(period.to.slice(0, 4));
    const years: number[] = [];
    for (let year = start; year <= end; year += 1) {
      years.push(year);
    }
    return years;
  }
  return [...new Set(uniqueDays(rowDays).map((day) => Number(day.slice(0, 4))))].sort((a, b) => a - b);
}

export function formatIsoDay(day: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : day;
}

/** Preview columns are capped. The metric often lives only in meta.available_columns or on a row. */
function datasetFieldKeys(dataset: ChatDatasetArtifact): string[] {
  const keys: string[] = [];
  const add = (key: unknown) => {
    if (typeof key === 'string' && key.length > 0 && !keys.includes(key)) {
      keys.push(key);
    }
  };
  for (const column of dataset.columns ?? []) {
    add(column);
  }
  for (const row of dataset.preview_rows ?? []) {
    for (const key of Object.keys(row)) {
      add(key);
    }
  }
  const available = dataset.meta?.['available_columns'];
  if (Array.isArray(available)) {
    for (const item of available) {
      if (item && typeof item === 'object') {
        add((item as { key?: unknown }).key);
      }
    }
  }
  return keys;
}

function indexTypesFrom(dataset: ChatDatasetArtifact): string[] {
  const found: string[] = [];
  for (const row of dataset.preview_rows ?? []) {
    const raw = text(row['index_type'])?.toLowerCase();
    const mapped = raw ? INDEX_TYPE_BY_WIRE[raw] : null;
    if (mapped && !found.includes(mapped)) {
      found.push(mapped);
    }
  }
  return found;
}

function uniqueDays(values: string[]): string[] {
  const days = new Set<string>();
  for (const value of values) {
    const day = toIsoDay(value);
    if (day) {
      days.add(day);
    }
  }
  return [...days].sort();
}

export function weatherLayerNames(text: string | null | undefined): string[] {
  if (!text) {
    return [];
  }
  WEATHER_LAYER_NAME.lastIndex = 0;
  const found: string[] = [];
  for (const match of text.matchAll(WEATHER_LAYER_NAME)) {
    if (!found.includes(match[1])) {
      found.push(match[1]);
    }
  }
  return found;
}

/** Same tile address the weather map uses: /tiles/Map/GetTile. */
export function weatherTileUrl(apiBase: string, layer: string, coords: { x: number; y: number; z: number }): string {
  const { lat, lon, zoom } = xyzToLatLon(coords);
  const base = apiBase.replace(/\/$/, '');
  return `${base}/tiles/Map/GetTile?lat=${lat}&lon=${lon}&z=${zoom}&layer=${encodeURIComponent(layer)}&coordinateSystem=0`;
}

export function isIndexLayerName(name: string): boolean {
  return INDEX_LAYER_NAME.test(name);
}

/**
 * Same tile address IndexManager uses for a dated index mosaic: workspace indexint8 and the
 * {INDEX}_int8 style. Any other layer name is requested as is.
 */
export function indexTileUrl(apiBase: string, layer: string, coords: { x: number; y: number; z: number }): string {
  const { lat, lon, zoom } = xyzToLatLon(coords);
  const base = apiBase.replace(/\/$/, '');
  const dated = /^([A-Za-z]+)(\d+)$/.exec(layer);
  if (!dated) {
    return `${base}/tiles/Map/GetTile?lat=${lat}&lon=${lon}&z=${zoom}&layer=${encodeURIComponent(layer)}&coordinateSystem=0`;
  }
  const style = `${dated[1]}_int8`;
  return `${base}/tiles/Map/GetTile?lat=${lat}&lon=${lon}&z=${zoom}&layer=${encodeURIComponent(`indexint8:${layer}`)}&coordinateSystem=0&style=${encodeURIComponent(style)}`;
}

export function numericColumns(rows: Array<Record<string, unknown>>, columns: string[]): string[] {
  const names = columns.length > 0 ? columns : Object.keys(rows[0] ?? {});
  return names.filter((name) => !ID_COLUMNS.has(name) && rows.some((row) => readNumber(row[name]) !== null));
}

export function defaultMetric(columns: string[]): string | null {
  for (const preferred of METRIC_PRIORITY) {
    if (columns.includes(preferred)) {
      return preferred;
    }
  }
  return columns[0] ?? null;
}

export function metricLabel(metric: string): string {
  return METRIC_LABELS[metric] ?? metric;
}

export function colorForValue(value: number, min: number, max: number): string {
  const span = max - min;
  const t = span <= 0 ? 1 : Math.min(1, Math.max(0, (value - min) / span));
  return mixHex('#e8f5e4', '#145214', t);
}

/** Five equal steps from min to max, including both ends. */
export function legendStops(min: number, max: number): AnalyticsLegendStop[] {
  const last = LEGEND_STOP_COUNT - 1;
  const stops: AnalyticsLegendStop[] = [];
  for (let index = 0; index < LEGEND_STOP_COUNT; index += 1) {
    const t = last === 0 ? 0 : index / last;
    const value = min + (max - min) * t;
    stops.push({ value, color: colorForValue(value, min, max) });
  }
  return stops;
}

export function isAreaMetric(metric: string): boolean {
  return metric === 'area_ha' || metric.endsWith('_area_ha');
}

/** Area column a density overlay divides by. The selected area column wins. */
export function areaSourceForDensity(columns: readonly string[], preferred: string | null): string | null {
  if (preferred && isAreaMetric(preferred) && columns.includes(preferred)) {
    return preferred;
  }
  for (const name of METRIC_PRIORITY) {
    if (isAreaMetric(name) && columns.includes(name)) {
      return name;
    }
  }
  return columns.find((column) => isAreaMetric(column)) ?? null;
}

/** True when a district contour exists, so area can be turned into hectares per km². */
export function canOfferDensity(
  maps: ChatMapArtifact[] | null | undefined,
  columns: readonly string[]
): boolean {
  if (!columns.some((column) => isAreaMetric(column))) {
    return false;
  }
  return (maps ?? []).some((map) =>
    map.kind === 'districts' && (map.districts ?? []).some((item) => geoJsonToLeaflet(item['geom']) !== null)
  );
}

/** Crop named by the answer itself. A crop id on an anomaly row is not a sowing filter. */
export function datasetCropId(dataset: ChatDatasetArtifact | null | undefined): string | null {
  return text(dataset?.meta?.['crop_id']);
}

export function legendHint(metric: string): string {
  if (metric === DENSITY_METRIC) {
    return 'Чем темнее район, тем выше плотность посевов на его площади.';
  }
  return 'Чем темнее район, тем больше значение показателя.';
}

/**
 * Filters for planned or actual sowings inside a clicked district.
 * The dataset period is not a planting window: an anomaly or weather range would hide the fields.
 * A crop is applied only when the answer itself names one (`meta.crop_id`).
 */
export function seasonLayerFilters(
  planned: boolean,
  regionId: string,
  cropId: string | null
): SeasonLayerFilter[] {
  const filters: SeasonLayerFilter[] = [
    { filterField: 'regionId', filterOperator: FilterOperator.Equals, filterValue: regionId },
    { filterField: 'isPlanned', filterOperator: FilterOperator.Equals, filterValue: planned }
  ];
  if (cropId) {
    filters.push({
      filterField: planned ? 'plannedCropId' : 'CropId',
      filterOperator: FilterOperator.Equals,
      filterValue: cropId
    });
  }
  return filters;
}

/** Outer rings of a WKT polygon or multipolygon, as Leaflet [lat, lng] rings. */
export function wktToLeafletPolygons(value: string | null | undefined): [number, number][][] {
  if (!value || !value.trim()) {
    return [];
  }
  const text = value.trim();
  const upper = text.toUpperCase();
  if (upper.startsWith('MULTIPOLYGON')) {
    return multipolygonRings(text);
  }
  if (upper.startsWith('POLYGON')) {
    const ring = ringFromWktBody(text);
    return ring.length >= 3 ? [ring] : [];
  }
  const geometry = geoJsonToLeaflet(text);
  if (!geometry) {
    return [];
  }
  return geometry.wktCoordinates
    .map((rings) => rings[0])
    .filter((ring): ring is [number, number][] => !!ring && ring.length >= 3);
}

/** West/south/east/north of Leaflet [lat, lng] points. */
export function latLngBoundsBox(points: readonly [number, number][]): { west: number; east: number; south: number; north: number } | null {
  if (points.length === 0) {
    return null;
  }
  let south = points[0][0];
  let north = points[0][0];
  let west = points[0][1];
  let east = points[0][1];
  for (const [lat, lng] of points) {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      continue;
    }
    south = Math.min(south, lat);
    north = Math.max(north, lat);
    west = Math.min(west, lng);
    east = Math.max(east, lng);
  }
  if (![south, north, west, east].every((value) => Number.isFinite(value)) || south > north || west > east) {
    return null;
  }
  return { west, east, south, north };
}

/** Spherical area of a Leaflet polygon, holes subtracted, in square kilometres. */
export function polygonAreaKm2(geometry: LeafletGeometry): number {
  let area = 0;
  for (const rings of geometry.wktCoordinates) {
    if (rings.length === 0) {
      continue;
    }
    let polygon = ringAreaKm2(rings[0]);
    for (let index = 1; index < rings.length; index += 1) {
      polygon -= ringAreaKm2(rings[index]);
    }
    area += Math.max(0, polygon);
  }
  return area;
}

/** GeoJSON positions are [lon, lat]. Leaflet polygons are [lat, lng]. */
export function geoJsonToLeaflet(geom: unknown): LeafletGeometry | null {
  const parsed = typeof geom === 'string' ? parseJson(geom) : geom;
  if (!parsed || typeof parsed !== 'object') {
    return null;
  }
  const geometry = parsed as { type?: string; coordinates?: unknown; geometry?: unknown };
  if (geometry.type === 'Feature') {
    return geoJsonToLeaflet(geometry.geometry);
  }
  if (geometry.type === 'Polygon' && Array.isArray(geometry.coordinates)) {
    const rings = (geometry.coordinates as unknown[]).map(swapRing).filter((ring) => ring.length >= 3);
    if (rings.length === 0) {
      return null;
    }
    return { coordinates: rings[0], wktCoordinates: [rings] };
  }
  if (geometry.type === 'MultiPolygon' && Array.isArray(geometry.coordinates)) {
    const polygons = (geometry.coordinates as unknown[])
      .map((polygon) => (Array.isArray(polygon) ? polygon.map(swapRing).filter((ring) => ring.length >= 3) : []))
      .filter((polygon) => polygon.length > 0);
    if (polygons.length === 0 || polygons[0].length === 0) {
      return null;
    }
    return { coordinates: polygons[0][0], wktCoordinates: polygons };
  }
  return null;
}

export function readWms(map: ChatMapArtifact): AnalyticsWmsLayer | null {
  const source = map.wms;
  if (!source) {
    return null;
  }
  const wmsUrl = text(source['wms_url']);
  const layerName = text(source['layer_name']);
  if (!wmsUrl || !layerName) {
    return null;
  }
  return {
    id: 'analytics-wms',
    wmsUrl,
    layerName,
    style: text(source['style']),
    time: text(source['time_dimension']),
    bbox: text(source['bbox'])
  };
}

export function wmsTileUrl(layer: AnalyticsWmsLayer, coords: { x: number; y: number; z: number }): string {
  const params = new URLSearchParams({
    service: 'WMS',
    version: '1.1.1',
    request: 'GetMap',
    layers: layer.layerName,
    styles: layer.style ?? '',
    bbox: tileBbox3857(coords.x, coords.y, coords.z),
    width: '256',
    height: '256',
    srs: 'EPSG:3857',
    format: 'image/png',
    transparent: 'true'
  });
  if (layer.time) {
    params.set('TIME', layer.time);
  }
  const joiner = layer.wmsUrl.includes('?') ? '&' : '?';
  return `${layer.wmsUrl}${joiner}${params.toString()}`;
}

export function tileBbox3857(x: number, y: number, z: number): string {
  const span = (WORLD_HALF * 2) / 2 ** z;
  const minX = -WORLD_HALF + x * span;
  const maxY = WORLD_HALF - y * span;
  return `${minX},${maxY - span},${minX + span},${maxY}`;
}

export function buildAnalyticsMapScene(
  maps: ChatMapArtifact[] | null | undefined,
  dataset: ChatDatasetArtifact | null,
  metric: string | null,
  densitySource: string | null = null
): AnalyticsMapScene {
  const drafts: Array<Omit<AnalyticsMapPolygon, 'fillColor' | 'strokeColor'> & { value: number | null }> = [];
  const anomalies: AnalyticsMapPolygon[] = [];
  const anomalyPalette = new Map<string, string>();
  const pointDrafts: Array<Omit<AnalyticsMapPoint, 'fillColor'> & { value: number | null }> = [];
  const wms: AnalyticsWmsLayer[] = [];
  const warnings: string[] = [];
  const previewRows = dataset?.preview_rows ?? [];
  const byRegion = new Map<string, Record<string, unknown>>();
  for (const row of coloringRows(dataset)) {
    const regionId = text(row['region_id']);
    if (regionId) {
      byRegion.set(regionId.toLowerCase(), row);
    }
  }

  // Rows already placed as a polygon or point: a table row with the same id must not add a second marker.
  const placed = new Set<string>();
  let polygonIndex = 0;
  let pointIndex = 0;

  const place = (item: Record<string, unknown>, joined: Record<string, unknown> | undefined, fallbackName: string | null) => {
    const source = joined ?? item;
    const identity = rowKey(item);
    const geometry = geoJsonToLeaflet(item['geom']);
    const regionId = text(item['region_id']) ?? text(source['region_id']);
    const value = sceneMetricValue(source, metric, geometry, densitySource);
    if (geometry && isAnomalyRow(item)) {
      const type = text(item['anomaly_type']) ?? 'Аномалия';
      const html = anomalyPopup(item);
      anomalies.push({
        id: `analytics-anomaly-${anomalies.length}`,
        name: type,
        kind: 'anomaly',
        coordinates: geometry.coordinates,
        wktCoordinates: geometry.wktCoordinates,
        fillColor: anomalyColor(type, anomalyPalette),
        strokeColor: ANOMALY_STROKE,
        popup: html,
        tooltip: html,
        regionId: null
      });
      if (identity) {
        placed.add(identity);
      }
      return;
    }
    if (geometry) {
      const name = placeName(source) || placeName(item) || fallbackName || `Полигон ${polygonIndex + 1}`;
      const html = popupHtml(name, metric, value);
      drafts.push({
        id: `analytics-poly-${polygonIndex}`,
        name,
        kind: 'area',
        coordinates: geometry.coordinates,
        wktCoordinates: geometry.wktCoordinates,
        popup: html,
        tooltip: html,
        regionId,
        value
      });
      polygonIndex += 1;
      if (identity) {
        placed.add(identity);
      }
      return;
    }
    const centroid = readCentroid(item) ?? readCentroid(joined);
    if (!centroid || (identity && placed.has(identity))) {
      return;
    }
    const name = placeName(source) || placeName(item) || fallbackName || `Точка ${pointIndex + 1}`;
    const html = popupHtml(name, metric, value);
    pointDrafts.push({
      id: `analytics-point-${pointIndex}`,
      name,
      lat: centroid[0],
      lng: centroid[1],
      popup: html,
      tooltip: html,
      value
    });
    pointIndex += 1;
    if (identity) {
      placed.add(identity);
    }
  };

  (maps ?? []).forEach((map, mapIndex) => {
    warnings.push(...(map.warnings ?? []).filter((warning) => warning.trim().length > 0));
    if (map.kind === 'wms') {
      const layer = readWms(map);
      if (layer) {
        wms.push({ ...layer, id: `analytics-wms-${mapIndex}` });
      }
      return;
    }

    const items = map.kind === 'districts' ? map.districts ?? [] : map.kind === 'features' ? map.features ?? [] : [];
    items.forEach((item) => {
      const regionId = text(item['region_id']);
      const joined = map.kind === 'districts' && regionId ? byRegion.get(regionId.toLowerCase()) : undefined;
      place(item, joined, regionId);
    });
  });

  // Table rows that carry their own geometry or centroid (find_fields, anomalies without a map artifact).
  for (const row of previewRows) {
    const identity = rowKey(row);
    if (identity && placed.has(identity)) {
      continue;
    }
    if (geoJsonToLeaflet(row['geom']) === null && readCentroid(row) === null) {
      continue;
    }
    place(row, undefined, text(row['field_id']) ?? text(row['region_id']));
  }

  const values = [...drafts, ...pointDrafts].map((draft) => draft.value).filter((value): value is number => value !== null);
  const min = values.length ? Math.min(...values) : 0;
  const max = values.length ? Math.max(...values) : 0;
  const areas: AnalyticsMapPolygon[] = drafts.map((draft) => ({
    id: draft.id,
    name: draft.name,
    kind: draft.kind,
    coordinates: draft.coordinates,
    wktCoordinates: draft.wktCoordinates,
    fillColor: draft.value === null ? '#c5d5c4' : colorForValue(draft.value, min, max),
    strokeColor: '#145214',
    popup: draft.popup,
    tooltip: draft.tooltip,
    regionId: draft.regionId
  }));
  const points = pointDrafts.map((draft) => ({
    id: draft.id,
    name: draft.name,
    lat: draft.lat,
    lng: draft.lng,
    fillColor: draft.value === null ? '#2e8f27' : colorForValue(draft.value, min, max),
    popup: draft.popup,
    tooltip: draft.tooltip
  }));
  // Anomalies are drawn above the field they belong to, so they come last.
  const polygons = [...areas, ...anomalies];
  const areaBounds = areas.flatMap((polygon) => polygon.wktCoordinates.flatMap((rings) => rings.flat()));
  const pointBounds = points.map((point): [number, number] => [point.lat, point.lng]);
  // The view fits the field or district; anomaly polygons set the extent only when nothing else is drawn.
  const placeBounds = [...areaBounds, ...pointBounds];
  const anomalyBounds = anomalies.flatMap((polygon) => polygon.wktCoordinates.flatMap((rings) => rings.flat()));
  const drawnBounds = placeBounds.length >= 1 ? placeBounds : anomalyBounds;
  const bounds = drawnBounds.length >= 1 ? drawnBounds : wms.flatMap((layer) => bboxCorners(layer.bbox));
  return {
    polygons,
    points,
    wms,
    legend: metric && values.length
      ? { metric, label: metricLabel(metric), hint: legendHint(metric), min, max, stops: legendStops(min, max) }
      : null,
    anomalyLegend: [...anomalyPalette.entries()].map(([type, color]) => ({ type, color })),
    bounds,
    warnings
  };
}

function multipolygonRings(wkt: string): [number, number][][] {
  const body = wkt.slice(wkt.indexOf('('));
  const rings: [number, number][][] = [];
  const polygonPattern = /\(\(([^()]*)\)\)/g;
  for (const match of body.matchAll(polygonPattern)) {
    const ring = parseLngLatList(match[1]);
    if (ring.length >= 3) {
      rings.push(ring);
    }
  }
  return rings;
}

function ringFromWktBody(wkt: string): [number, number][] {
  const match = wkt.match(/\(\(([^()]*)\)/);
  return match ? parseLngLatList(match[1]) : [];
}

function parseLngLatList(body: string): [number, number][] {
  return body.split(',')
    .map((pair) => pair.trim().split(/\s+/).map(Number))
    .filter((pair) => pair.length >= 2 && Number.isFinite(pair[0]) && Number.isFinite(pair[1]))
    .map((pair) => [pair[1], pair[0]] as [number, number]);
}

function coloringRows(dataset: ChatDatasetArtifact | null): Array<Record<string, unknown>> {
  const mapRows = dataset?.map_rows;
  if (mapRows && mapRows.length > 0) {
    return mapRows;
  }
  return dataset?.preview_rows ?? [];
}

function sceneMetricValue(
  source: Record<string, unknown>,
  metric: string | null,
  geometry: LeafletGeometry | null,
  densitySource: string | null
): number | null {
  if (!metric) {
    return null;
  }
  if (metric !== DENSITY_METRIC) {
    return readNumber(source[metric]);
  }
  if (!densitySource || !geometry) {
    return null;
  }
  const area = readNumber(source[densitySource]);
  const km2 = polygonAreaKm2(geometry);
  if (area === null || km2 <= 0) {
    return null;
  }
  return area / km2;
}

function ringAreaKm2(ring: [number, number][]): number {
  const points = openRing(ring);
  if (points.length < 3) {
    return 0;
  }
  let sum = 0;
  for (let index = 0; index < points.length; index += 1) {
    const [lat1, lon1] = points[index];
    const [lat2, lon2] = points[(index + 1) % points.length];
    const lat1Rad = lat1 * Math.PI / 180;
    const lat2Rad = lat2 * Math.PI / 180;
    sum += ((lon2 - lon1) * Math.PI / 180) * (2 + Math.sin(lat1Rad) + Math.sin(lat2Rad));
  }
  return Math.abs(sum * EARTH_RADIUS_KM * EARTH_RADIUS_KM / 2);
}

function openRing(ring: [number, number][]): [number, number][] {
  if (ring.length < 2) {
    return ring;
  }
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first[0] === last[0] && first[1] === last[1]) {
    return ring.slice(0, -1);
  }
  return ring;
}

function rowKey(row: Record<string, unknown>): string | null {
  const id = text(row['field_id']) ?? text(row['region_id']) ?? text(row['anomaly_id']) ?? text(row['id']);
  return id ? id.toLowerCase() : null;
}

function mapHasDrawnGeography(map: ChatMapArtifact): boolean {
  if (map.kind === 'wms' && readWms(map)) {
    return true;
  }
  const items = map.kind === 'districts' ? map.districts : map.kind === 'features' ? map.features : [];
  return (items ?? []).some((item) => geoJsonToLeaflet(item['geom']) !== null || readCentroid(item) !== null);
}

function xyzToLatLon(coords: { x: number; y: number; z: number }): { lat: number; lon: number; zoom: number } {
  const zoom = coords.z;
  const n = Math.PI - (2 * Math.PI * (coords.y + 0.5)) / 2 ** zoom;
  const lat = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
  const lon = ((coords.x + 0.5) / 2 ** zoom) * 360 - 180;
  return { lat, lon, zoom };
}

function placeName(row: Record<string, unknown> | undefined): string {
  if (!row) {
    return '';
  }
  // A field row carries its own name next to the district name_ru: the field wins.
  return (
    text(row['field_name']) ||
    (text(row['field_id']) ? text(row['name']) : null) ||
    text(row['name_ru']) ||
    text(row['name_uz']) ||
    text(row['name_en']) ||
    text(row['name']) ||
    text(row['anomaly_type']) ||
    ''
  );
}

function popupHtml(name: string, metric: string | null, value: number | null): string {
  const title = escapeHtml(name);
  if (!metric || value === null) {
    return title;
  }
  return `<strong>${title}</strong><br>${escapeHtml(metricLabel(metric))}: ${formatMetric(value)}`;
}

function formatMetric(value: number): string {
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(value);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function readNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function text(value: unknown): string | null {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

function swapRing(ring: unknown): [number, number][] {
  if (!Array.isArray(ring)) {
    return [];
  }
  return ring
    .filter((point): point is number[] => Array.isArray(point) && point.length >= 2)
    .map((point) => [Number(point[1]), Number(point[0])] as [number, number])
    .filter((point) => Number.isFinite(point[0]) && Number.isFinite(point[1]));
}

function bboxCorners(bbox: string | null): [number, number][] {
  if (!bbox) {
    return [];
  }
  const parts = bbox.split(',').map((item) => Number(item.trim()));
  if (parts.length !== 4 || parts.some((item) => !Number.isFinite(item))) {
    return [];
  }
  const [minLon, minLat, maxLon, maxLat] = parts;
  return [
    [minLat, minLon],
    [maxLat, maxLon]
  ];
}

function parseJson(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function mixHex(from: string, to: string, t: number): string {
  const start = hexRgb(from);
  const end = hexRgb(to);
  const channel = (index: number) => Math.round(start[index] + (end[index] - start[index]) * t);
  return `#${[channel(0), channel(1), channel(2)].map((part) => part.toString(16).padStart(2, '0')).join('')}`;
}

function hexRgb(hex: string): [number, number, number] {
  return [
    Number.parseInt(hex.slice(1, 3), 16),
    Number.parseInt(hex.slice(3, 5), 16),
    Number.parseInt(hex.slice(5, 7), 16)
  ];
}
